// Pure presentation: measured values stay distinct from unknown evidence.

// Enough hues to tell adjacent journeys apart, taken round-robin. Distinguishable on the
// portal's dark map without competing with the position pin, which stays HERE's own blue.
const RIDE_COLOURS = ['#4FC3F7', '#FFC46B', '#7ED321', '#FF8A80', '#C79BFF', '#4DD0E1'];

// EVERY RIDE THE API RETURNS IS DRAWN THE SAME. A day arrives with short zero-distance
// stubs beside the real haul, from a driver cycling the ignition, and it is tempting to
// mute them. Whether such a stub is a journey is an OPEN PRODUCT QUESTION -- see
// docs/specs/requirements/location-history.md -- and this screen is not where it gets
// decided. A ride with no distance reported is not the same as one that covered none.

// A ride is identified by when it began. Two rides cannot start at the same instant, and
// the row's slot is not identity -- a reloaded day shifts every slot by one and React
// would carry the old selection onto the wrong journey.
function rideKey(ride, index) {
  if (ride.start && ride.start.ts) {
    return ride.start.ts;
  }
  if (ride.end && ride.end.ts) {
    return 'end-' + ride.end.ts;
  }
  return 'ride-' + index;
}


// Use the same recorded minute boundaries the table displays, with UTC arithmetic so
// midnight and daylight-saving transitions retain their elapsed interval.
function rideElapsedSeconds(ride) {
  const start = ride.start && Date.parse(ride.start.ts);
  const end = ride.end && Date.parse(ride.end.ts);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) { return null; }
  return (Math.floor(end / 60000) - Math.floor(start / 60000)) * 60;
}

function isKnownDistance(km) {
  return Number.isFinite(km) && km >= 0;
}

function averageSpeedKmh(km, seconds) {
  return isKnownDistance(km) && Number.isFinite(seconds) && seconds > 0
    ? (km / seconds) * 3600 : null;
}

// WHAT THE VEHICLE REPORTED, NOT WHAT WE CAN INFER FROM IT. A missing number stays
// missing: an odometer nobody read is not zero, and a fleet manager reading 0 km/h
// would take it for a measurement.
//
// THE FASTEST SAMPLE IS NOT THE TOP SPEED, AND THE LABEL SAYS SO. We look every few
// minutes, so a motorway stretch between two samples never happened as far as a maximum
// is concerned: this is the fastest reading TAKEN, never the fastest the vehicle went.
// The screen carried nothing at all until it was asked for; the wording is what keeps a
// coincidence of the sampling interval from reading as a measurement of the peak.
function rideStats(ride) {
  // OVER THE JOURNEY, NOT OVER THE SAMPLES. Samples land ten minutes apart, so averaging
  // them weights a traffic light the same as an hour of motorway. Distance over duration
  // is what the journey actually did.
  const distance = ride.distance_km;
  const seconds = rideElapsedSeconds(ride);
  // A reading needs no ignition end to be real: the vehicle reported that speed at that
  // instant whatever we later decided the journey's edges were. So the maximum is gated
  // on having readings at all, and on nothing else. A ride whose samples all carry no
  // speed has none, which is not a zero.
  const speeds = (ride.samples || [])
    .map((sample) => sample.speed_kmh)
    .filter((speed) => Number.isFinite(speed));
  return {
    avgSpeedKmh: averageSpeedKmh(distance, seconds),
    fastestSampleKmh: speeds.length ? Math.max(...speeds) : null,
    odometerStart: (ride.start && ride.start.odometer_km !== undefined)
      ? ride.start.odometer_km : null,
    odometerEnd: (ride.end && ride.end.odometer_km !== undefined)
      ? ride.end.odometer_km : null,
  };
}

// The day's totals. A ride whose distance the vehicle never reported makes the TOTAL
// unknown rather than contributing a zero -- a sum that looks measured while part of it
// was never reported is the one number on this screen worth distrusting.
function dayTotals(rides) {
  let km = 0;
  let seconds = 0;
  let kmKnown = true;
  let secondsKnown = true;
  rides.forEach((ride) => {
    if (!isKnownDistance(ride.distance_km)) {
      kmKnown = false;
    } else {
      km += ride.distance_km;
    }
    const elapsed = rideElapsedSeconds(ride);
    if (elapsed === null) {
      secondsKnown = false;
    } else {
      seconds += elapsed;
    }
  });
  return {
    rides: rides.length,
    km: kmKnown ? km : null,
    seconds: secondsKnown ? seconds : null,
    avgSpeedKmh: averageSpeedKmh(kmKnown ? km : null, secondsKnown ? seconds : null),
  };
}

function formatDistance(km) {
  if (!isKnownDistance(km)) {
    return '—';
  }
  // Two decimals is a ten-metre resolution, which is already finer than the odometer the
  // number is derived from. The raw float carries a dozen digits of subtraction noise.
  return km.toFixed(2);
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) {
    return '—';
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) {
    return hours + ' h ' + minutes + ' min';
  }
  return minutes + ' min';
}

export { RIDE_COLOURS, rideKey, rideElapsedSeconds, rideStats,
  dayTotals, formatDistance, formatDuration };
